/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import * as RadixUIScrollArea from "@radix-ui/react-scroll-area";
import type { ComponentProps, RefObject } from "react";
import { forwardRef } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import {
  colors,
  radius,
  spacing,
  stroke,
  zIndices,
} from "../../../../../global/emotion";
import { useLocalRef } from "../../../../hooks/useLocalRef";
import type { UseScrollAreaProps } from "../../../../hooks/useScrollArea";
import { useScrollArea } from "../../../../hooks/useScrollArea";
import type { MaskProps } from "../../../_base/_core/Mask/Mask.emotion";
import { Mask } from "../../../_base/_core/Mask/Mask.emotion";

const maskStyle: Interpolation = {
  pointerEvents: "none",
  position: "absolute",
  transition: "opacity 250ms ease-out",
  zIndex: zIndices.mask,
};
const maskBottomStyle: Interpolation<{ canScrollBottom: boolean }> = (
  options,
) => ({
  bottom: spacing.none,
  opacity: options.canScrollBottom ? 1 : 0,
});
const maskLeftStyle: Interpolation<{ canScrollLeft: boolean }> = (options) => ({
  left: spacing.none,
  opacity: options.canScrollLeft ? 1 : 0,
  top: spacing.none,
});
const maskRightStyle: Interpolation<{ canScrollRight: boolean }> = (
  options,
) => ({
  opacity: options.canScrollRight ? 1 : 0,
  right: spacing.none,
  top: spacing.none,
});
const maskTopStyle: Interpolation<{ canScrollTop: boolean }> = (options) => ({
  opacity: options.canScrollTop ? 1 : 0,
  top: spacing.none,
});
const rootStyle: Interpolation = {
  overflow: "hidden",
  position: "relative",
};
const rootStyleDisplayAsBlock: Interpolation = {
  "> div > div": {
    display: "block !important",
  },
};
const scrollbarStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: colors.transparent,
  ":hover": { backgroundColor: theme.semanticColors.neutralsLowest },
  display: "flex",
  touchAction: "none",
  transition:
    "background 200ms ease-out, border-color 200ms ease-out, opacity 200ms ease-out",
  userSelect: "none",
  zIndex: zIndices.scrollbar,
});
const scrollbarHorizontalStyle: Interpolation<ThemeType> = (theme) => ({
  borderTopColor: colors.transparent,
  ":hover": { backgroundColor: theme.semanticColors.neutralsLower },
  borderTopStyle: "solid",
  borderTopWidth: stroke.light,
  paddingBottom: spacing.xxs,
  paddingLeft: spacing.xs,
  paddingRight: spacing.xs,
  paddingTop: spacing.xxs,
});
const scrollbarOpacityStyle: Interpolation<{ opacity: number }> = (
  options,
) => ({
  opacity: options.opacity,
});
const scrollbarOpacityWithHoverStyle: Interpolation<{ opacity: number }> = (
  options,
) => ({
  opacity: options.opacity,
  ":hover": { opacity: 1 },
});
const scrollbarVerticalStyle: Interpolation<ThemeType> = (theme) => ({
  borderLeftColor: colors.transparent,
  ":hover": { borderLeftColor: theme.semanticColors.neutralsLower },
  borderLeftStyle: "solid",
  borderLeftWidth: stroke.light,
  paddingBottom: spacing.t,
  paddingLeft: spacing.xxs,
  paddingRight: spacing.xxs,
  paddingTop: spacing.t,
});
const thumbStyle: Interpolation<ThemeType> = (theme) => ({
  background: theme.semanticColors.neutralsMedium,
  borderRadius: radius.full,
});
const thumbHorizontalStyle: Interpolation = {
  minHeight: spacing.xs,
};
const thumbVerticalStyle: Interpolation = {
  minWidth: spacing.xs,
};
const viewportStyle: Interpolation = {
  height: spacing.full,
  position: "relative",
  width: spacing.full,
};

export type ScrollAreaProps = Omit<
  ComponentProps<typeof RadixUIScrollArea.Root>,
  "type"
> & {
  dependencies?: UseScrollAreaProps["dependencies"];
  horizontal?: boolean;
  maskBottom?: boolean;
  maskBottomProps?: MaskProps;
  maskBottomCSS?: Interpolation;
  maskLeft?: boolean;
  maskLeftProps?: MaskProps;
  maskLeftCSS?: Interpolation;
  maskRight?: boolean;
  maskRightProps?: MaskProps;
  maskRightCSS?: Interpolation;
  masksColor?: MaskProps["color"];
  masksSize?: MaskProps["size"];
  maskTop?: boolean;
  maskTopProps?: MaskProps;
  maskTopCSS?: Interpolation;
  scrollbarHorizontalProps?: ComponentProps<typeof RadixUIScrollArea.Scrollbar>;
  scrollbarHorizontalCSS?: Interpolation;
  scrollbarVerticalProps?: ComponentProps<typeof RadixUIScrollArea.Scrollbar>;
  scrollbarVerticalCSS?: Interpolation;
  showScrollbarsOnHover?: boolean;
  tableMarginAuto?: boolean;
  thumbHorizontalProps?: ComponentProps<typeof RadixUIScrollArea.Thumb>;
  thumbHorizontalCSS?: Interpolation;
  thumbVerticalProps?: ComponentProps<typeof RadixUIScrollArea.Thumb>;
  thumbVerticalCSS?: Interpolation;
  vertical?: boolean;
  viewportProps?: ComponentProps<typeof RadixUIScrollArea.Viewport>;
  viewportCSS?: Interpolation;
  rootDisplayAsBlock?: boolean;
};

export const ScrollArea = forwardRef<HTMLDivElement, ScrollAreaProps>(
  (
    {
      children,
      dependencies = [],
      horizontal = true,
      maskBottom,
      maskBottomProps,
      maskBottomCSS = {},
      maskLeft,
      maskLeftProps,
      maskLeftCSS = {},
      maskRight,
      maskRightProps,
      maskRightCSS = {},
      masksColor,
      masksSize,
      maskTop,
      maskTopProps,
      maskTopCSS = {},
      scrollbarHorizontalProps,
      scrollbarHorizontalCSS = {},
      scrollbarVerticalProps,
      scrollbarVerticalCSS = {},
      showScrollbarsOnHover = false,
      tableMarginAuto = false,
      thumbHorizontalProps,
      thumbHorizontalCSS = {},
      thumbVerticalProps,
      thumbVerticalCSS = {},
      vertical = true,
      viewportProps,
      viewportCSS = {},
      // this is to deal with this issue: https://github.com/radix-ui/primitives/issues/2722
      rootDisplayAsBlock = false,
      ...props
    },
    ref,
  ) => {
    const viewportRef = useLocalRef(ref);

    const {
      canScrollBottom,
      canScrollLeft,
      canScrollRight,
      canScrollTop,
      scrollingX,
      scrollingY,
    } = useScrollArea({
      dependencies,
      viewportRef:
        (viewportProps?.ref as RefObject<HTMLElement>) ?? viewportRef,
    });

    return (
      <RadixUIScrollArea.Root
        type="scroll"
        css={[rootStyle, rootDisplayAsBlock && rootStyleDisplayAsBlock]}
        {...props}
      >
        {maskBottom && (
          <Mask
            direction="to top"
            css={[
              maskStyle,
              maskBottomStyle({ canScrollBottom }),
              maskBottomCSS,
            ]}
            {...{ color: masksColor, size: masksSize, ...maskBottomProps }}
          />
        )}
        {maskLeft && (
          <Mask
            direction="to right"
            orientation="vertical"
            css={[maskStyle, maskLeftStyle({ canScrollLeft }), maskLeftCSS]}
            {...{ color: masksColor, size: masksSize, ...maskLeftProps }}
          />
        )}
        {maskRight && (
          <Mask
            direction="to left"
            orientation="vertical"
            css={[maskStyle, maskRightStyle({ canScrollRight }), maskRightCSS]}
            {...{ color: masksColor, size: masksSize, ...maskRightProps }}
          />
        )}
        {maskTop && (
          <Mask
            css={[maskStyle, maskTopStyle({ canScrollTop }), maskTopCSS]}
            {...{ color: masksColor, size: masksSize, ...maskTopProps }}
          />
        )}
        <RadixUIScrollArea.Viewport
          data-scroll-area-table-margin-auto={tableMarginAuto ? "" : null}
          css={[viewportStyle, viewportCSS]}
          {...{ ...viewportProps, ref: viewportProps?.ref ?? viewportRef }}
        >
          {children}
        </RadixUIScrollArea.Viewport>
        {horizontal && (
          <RadixUIScrollArea.Scrollbar
            forceMount
            orientation="horizontal"
            css={[
              scrollbarStyle,
              scrollbarHorizontalStyle,
              showScrollbarsOnHover
                ? scrollbarOpacityWithHoverStyle({
                    opacity: scrollingX ? 1 : 0,
                  })
                : scrollbarOpacityStyle({ opacity: scrollingX ? 1 : 0 }),
              scrollbarHorizontalCSS,
            ]}
            {...scrollbarHorizontalProps}
          >
            <RadixUIScrollArea.Thumb
              css={[thumbStyle, thumbHorizontalStyle, thumbHorizontalCSS]}
              {...thumbHorizontalProps}
            />
          </RadixUIScrollArea.Scrollbar>
        )}
        {vertical && (
          <RadixUIScrollArea.Scrollbar
            forceMount
            orientation="vertical"
            css={[
              scrollbarStyle,
              showScrollbarsOnHover
                ? scrollbarOpacityWithHoverStyle({
                    opacity: scrollingY ? 1 : 0,
                  })
                : scrollbarOpacityStyle({ opacity: scrollingY ? 1 : 0 }),
              scrollbarVerticalStyle,
              scrollbarVerticalCSS,
            ]}
            {...scrollbarVerticalProps}
          >
            <RadixUIScrollArea.Thumb
              css={[thumbStyle, thumbVerticalStyle, thumbVerticalCSS]}
              {...thumbVerticalProps}
            />
          </RadixUIScrollArea.Scrollbar>
        )}
      </RadixUIScrollArea.Root>
    );
  },
);

ScrollArea.displayName = "ScrollArea";
